/**
 * Report Parameter Type Definitions
 *
 * Uses discriminated unions so each parameter type has its own strongly-typed configuration.
 */

import type { ManipulateType } from 'dayjs';

// =============================================================================
// Date/Time Utilities
// =============================================================================

/**
 * Represents a date offset relative to today
 * Positive values = future, negative values = past
 */
export interface RelativeDateOffset {
  value: number;
  unit: ManipulateType;
}

/**
 * Preset date range options for quick selection
 */
export type DateRangePreset =
  | 'all_time'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'last_7_days'
  | 'last_30_days'
  | 'last_90_days';

// =============================================================================
// Parameter Configurations
// =============================================================================

/**
 * Configuration for date parameters
 */
export interface DateConfig {
  /** Default date offset relative to today */
  defaultOffset?: RelativeDateOffset;
  /** Minimum allowed date offset relative to today */
  minDateOffset?: RelativeDateOffset;
  /** Maximum allowed date offset relative to today */
  maxDateOffset?: RelativeDateOffset;
  /** Whether to allow selecting future dates */
  allowFutureDates?: boolean;
}

/**
 * Configuration for datetime parameters
 */
export interface DateTimeConfig extends DateConfig {
  /** Default time in HH:mm format */
  defaultTime?: string;
}

/**
 * Configuration for date range parameters
 */
export interface DateRangeConfig {
  /** Default start date offset relative to today */
  defaultStartOffset?: RelativeDateOffset;
  /** Default end date offset relative to today */
  defaultEndOffset?: RelativeDateOffset;
  /** Minimum allowed date offset relative to today */
  minDateOffset?: RelativeDateOffset;
  /** Maximum allowed date offset relative to today */
  maxDateOffset?: RelativeDateOffset;
  /** Whether to allow selecting future dates */
  allowFutureDates?: boolean;
  /** Preset date ranges to show as quick options */
  presets?: DateRangePreset[];
}

/**
 * Configuration for datetime range parameters
 */
export interface DateTimeRangeConfig extends DateRangeConfig {
  /** Default start time in HH:mm format */
  defaultStartTime?: string;
  /** Default end time in HH:mm format */
  defaultEndTime?: string;
}

/**
 * Option for select and multi-select parameters
 */
export interface SelectOption {
  /** The value submitted when this option is selected */
  value: string;
  /** Display label for the option */
  label: string;
  /** Optional group name for grouping related options */
  group?: string;
  /** Whether this option is disabled */
  disabled?: boolean;
}

/**
 * Configuration for single-select parameters
 */
export interface SelectConfig {
  /** Available options */
  options: SelectOption[];
  /** Default selected value */
  defaultValue?: string;
  /** Whether the selection can be cleared */
  clearable?: boolean;
  /** Placeholder text when no selection */
  placeholder?: string;
}

/**
 * Configuration for multi-select parameters
 */
export interface MultiSelectConfig {
  /** Available options */
  options: SelectOption[];
  /** Default selected values */
  defaultValue?: string[];
  /** Minimum number of selections required */
  minSelection?: number;
  /** Maximum number of selections allowed */
  maxSelection?: number;
  /** Whether to show a "Select All" option */
  includeSelectAll?: boolean;
  /** Placeholder text when no selection */
  placeholder?: string;
}

/**
 * Configuration for numeric parameters
 */
export interface NumericConfig {
  /** Default value */
  defaultValue?: number;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Step increment for the input */
  step?: number;
  /** Number of decimal places to display */
  decimalPlaces?: number;
  /** Unit label (e.g., '$', 'days', '%') */
  unit?: string;
  /** Position of the unit label */
  unitPosition?: 'prefix' | 'suffix';
}

/**
 * Configuration for boolean parameters
 */
export interface BooleanConfig {
  /** Default value */
  defaultValue?: boolean;
}

/**
 * Configuration for text parameters
 */
export interface TextConfig {
  /** Default value */
  defaultValue?: string;
  /** Minimum character length */
  minLength?: number;
  /** Maximum character length */
  maxLength?: number;
  /** Regex pattern for validation */
  pattern?: string;
  /** Custom error message for pattern validation */
  patternMessage?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to render as multiline textarea */
  multiline?: boolean;
}

// =============================================================================
// Base Parameter
// =============================================================================

/**
 * Base properties shared by all parameter types
 */
interface BaseParameter {
  /** Field name used in form data and API requests */
  name: string;
  /** Display label for the field */
  label: string;
  /** Help text shown below the field */
  helpText?: string;
  /** Whether the field is required */
  required: boolean;
  /** Display order within the form (lower = higher priority) */
  order?: number;
}

// =============================================================================
// Parameter Type Definitions (Discriminated Union)
// =============================================================================

/**
 * Single date selection parameter
 */
export interface DateParameter extends BaseParameter {
  type: 'date';
  config?: DateConfig;
}

/**
 * Date with time selection parameter
 */
export interface DateTimeParameter extends BaseParameter {
  type: 'datetime';
  config?: DateTimeConfig;
}

/**
 * Date range (start and end dates) parameter
 */
export interface DateRangeParameter extends BaseParameter {
  type: 'date_range';
  config?: DateRangeConfig;
}

/**
 * DateTime range (start and end dates with times) parameter
 */
export interface DateTimeRangeParameter extends BaseParameter {
  type: 'datetime_range';
  config?: DateTimeRangeConfig;
}

/**
 * Single selection dropdown parameter
 */
export interface SelectParameter extends BaseParameter {
  type: 'select';
  config: SelectConfig; // Required - must have options
}

/**
 * Multiple selection parameter
 */
export interface MultiSelectParameter extends BaseParameter {
  type: 'multi_select';
  config: MultiSelectConfig; // Required - must have options
}

/**
 * Numeric input parameter
 */
export interface NumericParameter extends BaseParameter {
  type: 'number';
  config?: NumericConfig;
}

/**
 * Boolean checkbox parameter
 */
export interface BooleanParameter extends BaseParameter {
  type: 'boolean';
  config?: BooleanConfig;
}

/**
 * Text input parameter
 */
export interface TextParameter extends BaseParameter {
  type: 'text';
  config?: TextConfig;
}

/**
 * Union type of all possible report parameters
 */
export type ReportParameter =
  | DateParameter
  | DateTimeParameter
  | DateRangeParameter
  | DateTimeRangeParameter
  | SelectParameter
  | MultiSelectParameter
  | NumericParameter
  | BooleanParameter
  | TextParameter;

/**
 * All possible parameter type strings
 */
export type ReportParameterType = ReportParameter['type'];

// =============================================================================
// Type Guards
// =============================================================================

export function isDateParameter(param: ReportParameter): param is DateParameter {
  return param.type === 'date';
}

export function isDateTimeParameter(param: ReportParameter): param is DateTimeParameter {
  return param.type === 'datetime';
}

export function isDateRangeParameter(param: ReportParameter): param is DateRangeParameter {
  return param.type === 'date_range';
}

export function isDateTimeRangeParameter(param: ReportParameter): param is DateTimeRangeParameter {
  return param.type === 'datetime_range';
}

export function isSelectParameter(param: ReportParameter): param is SelectParameter {
  return param.type === 'select';
}

export function isMultiSelectParameter(param: ReportParameter): param is MultiSelectParameter {
  return param.type === 'multi_select';
}

export function isNumericParameter(param: ReportParameter): param is NumericParameter {
  return param.type === 'number';
}

export function isBooleanParameter(param: ReportParameter): param is BooleanParameter {
  return param.type === 'boolean';
}

export function isTextParameter(param: ReportParameter): param is TextParameter {
  return param.type === 'text';
}

// =============================================================================
// Parameter Value Types
// =============================================================================

/**
 * Value types for each parameter type
 */
export interface ParameterValueMap {
  date: string | null;
  datetime: string | null;
  date_range: { start: string | null; end: string | null } | null;
  datetime_range: { start: string | null; end: string | null } | null;
  select: string | null;
  multi_select: string[];
  number: number | null;
  boolean: boolean;
  text: string | null;
}

/**
 * Get the value type for a specific parameter type
 */
export type ParameterValue<T extends ReportParameterType> = ParameterValueMap[T];

/**
 * Generic parameter values object for form submission
 */
export type ReportParameterValues = Record<string, unknown>;
